<script>
	/** @type {boolean | undefined} */
	export let disabled = undefined;

	/** @type {string | null | undefined} */
	export let href = '';

	/** @type {'primary' | 'secondary' | 'tertiary' | 'ghost' | 'ghost-alt' | 'success' | 'danger' | 'warning'} */
	export let kind = 'primary';

	/** @type {String} */
	export let label = '';

	/** @type {boolean} */
	export let loading = false;

	/** @type {boolean} */
	export let replaceState = false;

	/** @type {boolean} */
	export let reload = false;

	/** @type {'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'} */
	export let size = 'md';

	/**@type {string | undefined} */
	export let target = undefined;

	/**
	 * @type {String}
	 */
	export let type = '';

	/** @type {String} */
	export let tag = href && !disabled ? 'a' : 'button';

	/** @type {boolean} */
	export let wrap = false;

	/** @type {Object} */
	export let anchorAttrs = {
		href,
		role: href ? 'button' : undefined,
		target
	};

	/** @type {Object} */
	export let buttonAttrs = {
		disabled: disabled ?? undefined,
		type
	};

	/**
	 * Bypass function
	 * @param {any} obj
	 * @returns {any}
	 */
	function bypass(obj) {
		return obj;
	}
</script>

<svelte:element
	this={tag}
	data-sveltekit-replacestate={replaceState}
	data-sveltekit-reload={reload}
	class="button {kind} {size}"
	class:loading
	class:wrap
	data-testid="button"
	{...bypass(anchorAttrs)}
	{...bypass(buttonAttrs)}
	on:click
	on:keydown
	role={href ? 'button' : 'link'}
	tabindex="0"
>
	<slot name="left" />

	{#if label}
		{label}
	{:else}
		<slot />
	{/if}

	<slot name="right" />
</svelte:element>

<style>@keyframes opacity-pulse{0%,to{opacity:1}40%{opacity:.25}}.button{align-items:center;-webkit-appearance:none;-moz-appearance:none;appearance:none;background:linear-gradient(hsla(var(--hs-button-bg),var(--l-button-bg),var(--a-button-bg)),hsla(var(--hs-button-bg),var(--l-button-bg),var(--a-button-bg))),linear-gradient(hsla(var(--hsl-white)),hsla(var(--hsl-white)));color:hsla(var(--hsl-button-c));border:2px solid transparent;cursor:pointer;display:inline-flex;justify-content:center;gap:.375rem;font:var(--f-ui-md-semibold);text-align:center;transition:all var(--time-xs) ease-out}.button:not(.wrap){white-space:nowrap}.button.wrap{text-align:left}.button:where(.secondary,.tertiary,.ghost,.ghost-alt:hover){--hsl-button-c:var(--hsl-primary)}.button.ghost-alt:not(:hover){--hsl-button-c:var(--hsl-gray-extra-dark)}.button:where(.primary,.success,.danger,.warning){--hsl-button-c:var(--hsl-white)}.button:hover{--a-button-bg:var(--a-button-bg-hover)}.button:active{--a-button-bg:var(--a-button-bg-active)}.button.loading,.button[disabled]{cursor:not-allowed}.button[disabled]{background:hsla(var(--hsl-gray-light));color:hsla(var(--hsl-gray))}.button.loading{animation:opacity-pulse 1s infinite}:where(.primary,.secondary,.tertiary,.ghost,.ghost-alt){--hs-button-bg:var(--hs-primary);--l-button-bg:var(--l-primary)}.primary{--a-button-bg:1;--a-button-bg-hover:0.75;--a-button-bg-active:1}.secondary{border-color:hsla(var(--hsl-primary))}:not(.primary){--a-button-bg:0.2;--a-button-bg-hover:0.4;--a-button-bg-active:0.5}.ghost,.ghost-alt{--a-button-bg:0;--a-button-bg-hover:0.25;--a-button-bg-active:0.5}.success{--hsl-button-c:var(--hsl-success);--hs-button-bg:var(--hs-success);--l-button-bg:var(--l-success)}.danger{--hsl-button-c:var(--hsl-error);--hs-button-bg:var(--hs-error);--l-button-bg:var(--l-error)}.warning{--hsl-button-c:var(--hsl-warning);--hs-button-bg:var(--hs-warning);--l-button-bg:var(--l-warning)}.xs,.xxs{border-radius:.75rem;gap:.25rem;font:var(--f-ui-sm-semibold);padding:.25rem .375rem}.xs{padding:.375rem .5rem}.sm{border-radius:.875rem;gap:.375rem;padding:.5rem}.lg,.md{border-radius:1rem;padding:.625rem .75rem;gap:.5rem}.lg{padding:.75rem .875rem}.xl{border-radius:1.125rem;gap:.625rem;padding:.875rem 1rem}</style>
